import { useState } from 'react';
import { Calculator, ChevronDown, Info, Link2 } from 'lucide-react';
import { cn } from '@/shared/utils/utils';

interface Formula {
  label: string;
  formula: string;
  meaning?: string;
}

interface ModuleGuideProps {
  title?: string;
  summary: string;
  steps: string[];
  formulas?: Formula[];
  defaultOpen?: boolean;
}

export function ModuleGuide({
  title = 'How this section works',
  summary,
  steps,
  formulas = [],
  // Collapsed by default: the redesign keeps guidance secondary to the work
  // queue. The summary line stays visible and one click opens the steps.
  defaultOpen = false,
}: ModuleGuideProps) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <section className="rounded-xl border border-blue-200 bg-blue-50/70 dark:border-blue-900 dark:bg-blue-950/20">
      <button
        type="button"
        onClick={() => setOpen(value => !value)}
        className="flex w-full items-start gap-3 p-4 text-left"
        aria-expanded={open}
      >
        <span className="rounded-lg bg-blue-100 p-2 text-blue-700 dark:bg-blue-900/60 dark:text-blue-300">
          <Info className="h-4 w-4" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold text-foreground">{title}</span>
          <span className="mt-0.5 block text-xs leading-5 text-muted-foreground">{summary}</span>
        </span>
        <ChevronDown className={cn('mt-1 h-4 w-4 shrink-0 text-muted-foreground transition-transform', open && 'rotate-180')} />
      </button>

      {open && (
        <div className="border-t border-blue-200 px-4 pb-4 pt-3 dark:border-blue-900">
          <div className="grid gap-2 md:grid-cols-3">
            {steps.map((step, index) => (
              <div key={step} className="flex gap-2 rounded-lg bg-background/80 p-3 text-xs leading-5">
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground">{index + 1}</span>
                <span>{step}</span>
              </div>
            ))}
          </div>

          {formulas.length > 0 && (
            <div className="mt-3 grid gap-2 md:grid-cols-2">
              {formulas.map(item => (
                <div key={item.label} className="flex gap-2 rounded-lg border border-blue-100 bg-background/70 p-3 dark:border-blue-900/70">
                  <Calculator className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
                  <div className="text-xs">
                    <p className="font-semibold">{item.label}</p>
                    <p className="mt-0.5 font-mono text-blue-700 dark:text-blue-300">{item.formula}</p>
                    {item.meaning && <p className="mt-1 text-muted-foreground">{item.meaning}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}

          <p className="mt-3 flex items-center gap-1.5 text-xs text-muted-foreground">
            <Link2 className="h-3.5 w-3.5" /> Only posted/final records update connected reports, ledgers and stock; a draft is just a pending record.
          </p>
        </div>
      )}
    </section>
  );
}
